import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const C = {
  primary: '#0F6E56',
  primaryBg: '#E1F5EE',
  head: '#0F6E56',
  border: '#E3E1D9',
  text: '#1F1E1B',
  muted: '#5F5E5A',
  surface: '#F7F5F0',
  gold: '#91600C',
  danger: '#C0392B',
};

const API = '/api/forum2';

function authHeaders() {
  const token = localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

function ago(iso) {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 300) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

/* ---------- chhota markdown renderer (bold, italic, code, links, list) ---------- */
function inline(text, k0 = 0) {
  const RX = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(https?:\/\/[^\s]+)/g;
  const out = [];
  let k = k0, last = 0, m;
  while ((m = RX.exec(text)) !== null) {
    if (m.index > last) out.push(<span key={k++}>{text.slice(last, m.index)}</span>);
    const t = m[0];
    if (m[1]) out.push(<code key={k++} style={{ background: C.primaryBg, padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: 12.5 }}>{t.slice(1, -1)}</code>);
    else if (m[2]) out.push(<strong key={k++}>{t.slice(2, -2)}</strong>);
    else if (m[3]) out.push(<em key={k++}>{t.slice(1, -1)}</em>);
    else out.push(<a key={k++} href={t} target="_blank" rel="noopener noreferrer" style={{ color: C.primary, wordBreak: 'break-all' }}>{t}</a>);
    last = m.index + t.length;
  }
  if (last < text.length) out.push(<span key={k++}>{text.slice(last)}</span>);
  return out;
}

function Body({ text }) {
  if (!text) return null;
  return (
    <div style={{ fontSize: 14, lineHeight: 1.65, color: C.text }}>
      {text.split('\n').map((ln, i) => {
        if (ln.startsWith('### ')) return <h4 key={i} style={{ margin: '10px 0 4px', fontSize: 15 }}>{inline(ln.slice(4))}</h4>;
        if (ln.startsWith('## ')) return <h3 key={i} style={{ margin: '12px 0 5px', fontSize: 16 }}>{inline(ln.slice(3))}</h3>;
        if (ln.startsWith('# ')) return <h2 key={i} style={{ margin: '14px 0 6px', fontSize: 18 }}>{inline(ln.slice(2))}</h2>;
        if (ln === '---') return <hr key={i} style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '12px 0' }} />;
        if (ln.startsWith('> ')) return <blockquote key={i} style={{ borderLeft: `3px solid ${C.primary}`, paddingLeft: 10, margin: '6px 0', color: C.muted, fontStyle: 'italic' }}>{inline(ln.slice(2))}</blockquote>;
        if (ln.startsWith('- ')) return <div key={i} style={{ display: 'flex', gap: 6, margin: '3px 0 3px 6px' }}><span>{'\u2022'}</span><span>{inline(ln.slice(2))}</span></div>;
        if (ln.trim() === '') return <div key={i} style={{ height: 8 }} />;
        return <p key={i} style={{ margin: '4px 0' }}>{inline(ln)}</p>;
      })}
    </div>
  );
}

function Rank({ r }) {
  if (!r) return null;
  const s = r.stars || 0;
  return (
    <span title={`${r.rank} — activity ${r.activity}, merit ${r.merit}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
      <span style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{r.rank}</span>
      {s > 0 && <span style={{ fontSize: 9, color: s >= 5 ? C.gold : C.primary, letterSpacing: -1 }}>{r.icon.repeat(Math.min(s, 7))}</span>}
      {r.merit > 0 && <span style={{ fontSize: 9, color: C.gold }}>{'\u2B50'}{r.merit}</span>}
    </span>
  );
}

/* ==================== FORUM INDEX ==================== */
function Index() {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch(`${API}/index/`).then((r) => r.json()).then(setD)
      .catch(() => setD({ error: 'Could not load the forum.' }));
  }, []);

  if (!d) return <p style={{ color: C.muted, textAlign: 'center', padding: '2rem 0' }}>Loading forum…</p>;
  if (d.error) return <p style={{ color: C.danger, textAlign: 'center', padding: '2rem 0' }}>{d.error}</p>;

  const t = d.totals || {};

  return (
    <>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 11, color: C.muted }}>
        {t.threads} topics · {t.threads + t.replies} posts · {t.boards} boards · {t.members} members
      </div>

      {(d.categories || []).map((cat) => (
        <div key={cat.slug} style={{ marginBottom: 16, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ background: C.head, color: '#fff', padding: '7px 12px', fontSize: 13, fontWeight: 700 }}>
            {cat.name}
          </div>

          {cat.boards.length === 0 && (
            <p style={{ padding: '10px 12px', margin: 0, fontSize: 12, color: C.muted }}>No boards here yet.</p>
          )}

          {cat.boards.map((b, i) => (
            <div key={b.slug} style={{ borderTop: i ? `1px solid ${C.border}` : 'none', background: '#fff' }}>
              <div style={{ display: 'flex', gap: 10, padding: '10px 12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: 19, lineHeight: 1.1 }}>{b.icon || '\u{1F4C1}'}</span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={`/forum/b/${b.slug}`} style={{ fontSize: 14, fontWeight: 600, color: C.primary, textDecoration: 'none' }}>
                    {b.name}
                  </a>
                  {b.is_locked && <span style={{ fontSize: 10, color: C.muted, marginLeft: 6 }}>{'\u{1F512}'} read only</span>}
                  <p style={{ margin: '2px 0 0', fontSize: 11.5, color: C.muted, lineHeight: 1.45 }}>{b.description}</p>
                  {b.moderators.length > 0 && (
                    <p style={{ margin: '2px 0 0', fontSize: 10.5, color: C.muted, fontStyle: 'italic' }}>
                      Moderator{b.moderators.length > 1 ? 's' : ''}: {b.moderators.map((m) => '@' + m).join(', ')}
                    </p>
                  )}
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 62 }}>
                  <p style={{ margin: 0, fontSize: 11, color: C.muted }}>{b.posts} Posts</p>
                  <p style={{ margin: 0, fontSize: 11, color: C.muted }}>{b.topics} Topics</p>
                </div>
              </div>

              {b.children.length > 0 && (
                <div style={{ background: C.surface, padding: '5px 12px 6px 42px', fontSize: 11, color: C.muted }}>
                  Child boards:{' '}
                  {b.children.map((c, ci) => (
                    <span key={c.slug}>
                      {ci > 0 && ', '}
                      <a href={`/forum/b/${c.slug}`} style={{ color: C.primary, textDecoration: 'none' }}>{c.name}</a>
                    </span>
                  ))}
                </div>
              )}

              {b.last_post && (
                <div style={{ background: '#FBFAF7', padding: '5px 12px 6px 42px', fontSize: 10.5, color: C.muted }}>
                  Last post by <b>@{b.last_post.author}</b> in{' '}
                  <a href={`/forum/t/${b.last_post.thread_id}`} style={{ color: C.primary, textDecoration: 'none' }}>
                    {b.last_post.is_reply ? 'Re: ' : ''}{b.last_post.title}
                  </a>{' '}
                  · {ago(b.last_post.at)}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/* ==================== BOARD ==================== */
function BoardView({ slug }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/board/${slug}/`, { headers: authHeaders() })
      .then((r) => r.json()).then(setD)
      .catch(() => setD({ error: 'Could not load this board.' }));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <p style={{ color: C.muted, textAlign: 'center', padding: '2rem 0' }}>Loading…</p>;
  if (d.error) return <p style={{ color: C.danger, textAlign: 'center', padding: '2rem 0' }}>{d.error}</p>;

  const b = d.board;

  return (
    <>
      <p style={{ fontSize: 11.5, color: C.muted, margin: '0 0 10px' }}>
        <a href="/forum" style={{ color: C.primary, textDecoration: 'none' }}>Forum</a>
        {' \u203A '}{b.category}
        {b.parent && <>{' \u203A '}<a href={`/forum/b/${b.parent.slug}`} style={{ color: C.primary, textDecoration: 'none' }}>{b.parent.name}</a></>}
        {' \u203A '}<b>{b.name}</b>
      </p>

      <div style={{ background: C.head, color: '#fff', borderRadius: '8px 8px 0 0', padding: '8px 12px' }}>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{b.icon} {b.name}</p>
        <p style={{ margin: '2px 0 0', fontSize: 11, opacity: 0.85 }}>{b.description}</p>
      </div>

      {d.children && d.children.length > 0 && (
        <div style={{ border: `1px solid ${C.border}`, borderTop: 'none', background: C.surface, padding: '8px 12px' }}>
          {d.children.map((c) => (
            <div key={c.slug} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <a href={`/forum/b/${c.slug}`} style={{ fontSize: 12.5, color: C.primary, textDecoration: 'none' }}>
                {c.icon || '\u{1F4C1}'} {c.name}
              </a>
              <span style={{ fontSize: 10.5, color: C.muted }}>{c.topics} topics</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ border: `1px solid ${C.border}`, borderTop: 'none', borderRadius: '0 0 8px 8px', background: '#fff', padding: '8px 12px', marginBottom: 12 }}>
        {d.can_post ? (
          <a href={`/forum/new/${slug}`} style={{ fontSize: 12.5, fontWeight: 600, color: '#fff', background: C.primary, padding: '7px 14px', borderRadius: 7, textDecoration: 'none' }}>
            {'\u270D\uFE0F'} New topic
          </a>
        ) : (
          <span style={{ fontSize: 11.5, color: C.muted }}>
            {b.is_locked ? 'This board is read-only.' : 'Log in to post.'}
          </span>
        )}
      </div>

      {d.threads.length === 0 ? (
        <p style={{ textAlign: 'center', color: C.muted, fontSize: 13, padding: '2rem 0' }}>No topics here yet.</p>
      ) : (
        d.threads.map((t) => (
          <div key={t.id} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 11px', marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <a href={`/forum/t/${t.id}`} style={{ fontSize: 13.5, fontWeight: 600, color: C.text, textDecoration: 'none' }}>
                  {t.is_pinned && <span title="Pinned" style={{ marginRight: 4 }}>{'\u{1F4CC}'}</span>}
                  {t.is_locked && <span title="Locked" style={{ marginRight: 4 }}>{'\u{1F512}'}</span>}
                  {t.title}
                </a>
                <p style={{ margin: '3px 0 0', fontSize: 11, color: C.muted, display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>@{t.author}</span>
                  <Rank r={t.author_rank} />
                  <span>· {ago(t.created_at)}</span>
                </p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ margin: 0, fontSize: 11, color: C.muted }}>{t.replies} replies</p>
                <p style={{ margin: 0, fontSize: 11, color: C.muted }}>{t.views} views</p>
              </div>
            </div>
            {t.last_reply_by && (
              <p style={{ margin: '4px 0 0', fontSize: 10.5, color: C.muted }}>
                Last reply by <b>@{t.last_reply_by}</b> · {ago(t.last_reply_at)}
              </p>
            )}
          </div>
        ))
      )}
    </>
  );
}

/* ==================== THREAD ==================== */
function ThreadView({ id, user }) {
  const [d, setD] = useState(null);
  const [text, setText] = useState('');
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    fetch(`${API}/thread/${id}/`, { headers: authHeaders() })
      .then((r) => r.json()).then(setD)
      .catch(() => setD({ error: 'Could not load this thread.' }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (text.trim().length < 2) { setErr('Please write a message.'); return; }
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/thread/${id}/reply/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ body: text, quote_of: quote ? quote.id : null }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Could not post the reply.');
      setText(''); setQuote(null); load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const mod = async (action) => {
    if (action === 'delete' && !window.confirm('Delete this whole thread?')) return;
    try {
      const res = await fetch(`${API}/thread/${id}/admin/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ action }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Failed');
      if (j.deleted) { window.location.href = '/forum'; return; }
      load();
    } catch (e) { alert(e.message); }
  };

  if (!d) return <p style={{ color: C.muted, textAlign: 'center', padding: '2rem 0' }}>Loading…</p>;
  if (d.error) return <p style={{ color: C.danger, textAlign: 'center', padding: '2rem 0' }}>{d.error}</p>;

  const t = d.thread;
  const btn = { fontSize: 11, padding: '4px 9px', border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer', color: C.muted };

  const PostBox = ({ author, rank, when, body, quoted }) => (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
      <div style={{ background: C.surface, padding: '6px 11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text, display: 'flex', gap: 5, alignItems: 'center' }}>
          <a href={`/profile/${author}`} style={{ color: C.text, textDecoration: 'none' }}>@{author}</a>
          <Rank r={rank} />
        </span>
        <span style={{ fontSize: 10.5, color: C.muted }}>{ago(when)}</span>
      </div>
      <div style={{ padding: '10px 11px' }}>
        {quoted && (
          <div style={{ background: C.surface, borderLeft: `3px solid ${C.primary}`, padding: '6px 9px', marginBottom: 8, fontSize: 12, color: C.muted }}>
            <b>@{quoted.author}</b> wrote: {quoted.excerpt}…
          </div>
        )}
        <Body text={body} />
      </div>
    </div>
  );

  return (
    <>
      <p style={{ fontSize: 11.5, color: C.muted, margin: '0 0 10px' }}>
        <a href="/forum" style={{ color: C.primary, textDecoration: 'none' }}>Forum</a>
        {' \u203A '}{t.board.category}
        {' \u203A '}<a href={`/forum/b/${t.board.slug}`} style={{ color: C.primary, textDecoration: 'none' }}>{t.board.name}</a>
      </p>

      <h1 style={{ fontSize: 18, margin: '0 0 3px', color: C.text }}>
        {t.is_pinned && <span style={{ marginRight: 5 }}>{'\u{1F4CC}'}</span>}
        {t.is_locked && <span style={{ marginRight: 5 }}>{'\u{1F512}'}</span>}
        {t.title}
      </h1>
      <p style={{ fontSize: 11, color: C.muted, margin: '0 0 12px' }}>
        {t.views} views · {d.replies.length} replies
      </p>

      {d.can_moderate && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          <button onClick={() => mod(t.is_pinned ? 'unpin' : 'pin')} style={btn}>
            {'\u{1F4CC}'} {t.is_pinned ? 'Unpin' : 'Pin'}
          </button>
          <button onClick={() => mod(t.is_locked ? 'unlock' : 'lock')} style={btn}>
            {'\u{1F512}'} {t.is_locked ? 'Unlock' : 'Lock'}
          </button>
          <button onClick={() => mod('delete')} style={{ ...btn, color: C.danger, borderColor: C.danger }}>
            Delete
          </button>
        </div>
      )}

      <PostBox author={t.author} rank={t.author_rank} when={t.created_at} body={t.body} />

      {d.replies.map((r) => (
        <div key={r.id}>
          <PostBox author={r.author} rank={r.author_rank} when={r.created_at} body={r.body} quoted={r.quote_of} />
          {d.can_reply && (
            <button
              onClick={() => { setQuote(r); window.scrollTo(0, document.body.scrollHeight); }}
              style={{ ...btn, marginTop: -4, marginBottom: 10 }}
            >
              {'\u{1F4AC}'} Quote
            </button>
          )}
        </div>
      ))}

      {d.can_reply ? (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '11px', marginTop: 10 }}>
          <p style={{ margin: '0 0 6px', fontSize: 12.5, fontWeight: 600 }}>Post a reply</p>
          {quote && (
            <div style={{ background: C.surface, borderLeft: `3px solid ${C.primary}`, padding: '5px 8px', marginBottom: 7, fontSize: 11.5, color: C.muted }}>
              Quoting <b>@{quote.author}</b>
              <button onClick={() => setQuote(null)} style={{ marginLeft: 8, border: 'none', background: 'none', color: C.danger, cursor: 'pointer', fontSize: 11 }}>remove</button>
            </div>
          )}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder="Write your reply… (**bold**, *italic*, `code`, - list all work)"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: `1px solid ${C.border}`, borderRadius: 7, boxSizing: 'border-box', resize: 'vertical' }}
          />
          {err && <p style={{ color: C.danger, fontSize: 11.5, margin: '5px 0 0' }}>{err}</p>}
          <button
            onClick={send}
            disabled={busy}
            style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: '#fff', background: C.primary, border: 'none', borderRadius: 7, padding: '8px 18px', cursor: busy ? 'wait' : 'pointer' }}
          >
            {busy ? 'Posting…' : 'Post reply'}
          </button>
        </div>
      ) : (
        <p style={{ fontSize: 12, color: C.muted, textAlign: 'center', padding: '14px 0' }}>
          {t.is_locked ? 'This thread is locked.' : 'Log in to reply.'}
        </p>
      )}
    </>
  );
}

/* ==================== NAYA THREAD ==================== */
function NewThread({ slug }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/thread/new/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ board_slug: slug, title, body }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Could not create the topic.');
      window.location.href = `/forum/t/${j.thread_id}`;
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const inp = { width: '100%', padding: '9px 11px', fontSize: 13.5, border: `1px solid ${C.border}`, borderRadius: 7, boxSizing: 'border-box', marginBottom: 9 };

  return (
    <>
      <p style={{ fontSize: 11.5, color: C.muted, margin: '0 0 10px' }}>
        <a href="/forum" style={{ color: C.primary, textDecoration: 'none' }}>Forum</a>
        {' \u203A '}<a href={`/forum/b/${slug}`} style={{ color: C.primary, textDecoration: 'none' }}>{slug}</a>
        {' \u203A '}<b>New topic</b>
      </p>
      <h1 style={{ fontSize: 18, margin: '0 0 12px' }}>{'\u270D\uFE0F'} New topic</h1>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Topic title" style={inp} />
      <textarea
        value={body} onChange={(e) => setBody(e.target.value)} rows={11}
        placeholder="Write your post… (**bold**, *italic*, `code`, - list, > quote all work)"
        style={{ ...inp, resize: 'vertical' }}
      />
      {err && <p style={{ color: C.danger, fontSize: 12, margin: '0 0 8px' }}>{err}</p>}
      <button
        onClick={save} disabled={busy}
        style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', background: C.primary, border: 'none', borderRadius: 7, padding: '9px 20px', cursor: busy ? 'wait' : 'pointer' }}
      >
        {busy ? 'Posting…' : 'Post topic'}
      </button>
    </>
  );
}

/* ==================== MAIN ==================== */
export default function Forum({ user, mode }) {
  const params = useParams();

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '1.1rem 0.85rem 2rem', fontFamily: 'sans-serif' }}>
      {!mode && (
        <>
          <h1 style={{ fontSize: 20, margin: '0 0 3px' }}>{'\u{1F5E3}\uFE0F'} PenFlow Forum</h1>
          <p style={{ fontSize: 11.5, color: C.muted, margin: '0 0 12px' }}>
            Rank comes from steady activity and merit given by other members — it cannot be farmed or bought.
          </p>
        </>
      )}

      {mode === 'board'  && <BoardView slug={params.slug} />}
      {mode === 'thread' && <ThreadView id={params.id} user={user} />}
      {mode === 'new'    && <NewThread slug={params.slug} />}
      {!mode && <Index />}
    </div>
  );
}
